import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { useDispatch, useStore } from "react-redux";
import { LuBox, LuFileText, LuPenLine, LuWand } from "react-icons/lu";

import { approveJob } from "../../../common/server_utils.es6";
import { isCreditRefusal } from "../../utils/insufficientBalance";
import { recordPopupResult } from "../ai-chat/aiPopupResults";
import SceneVisualPreview from "./SceneVisualPreview";
import { addMediaLikeLibrary } from "./MotionAiEdit";
import {
  estimateSceneVisual,
  generateSceneVisual,
  SCENE_VISUAL_KINDS,
  SCENE_VISUAL_MODELS,
} from "./sceneVisualService";

/**
 * "3B Obje" and "Hareketli Görsel" — the two tabs of Görsel Ekle that produce
 * an HTML document instead of an image.
 *
 * ONE component for both kinds, because the author's side of them is the same
 * question asked twice: what do you want to see, in your words or in the
 * scene's narration. Everything that differs is a line in KINDS below — the
 * copy, the two extra motion controls, the route. Two near-identical
 * components would have drifted apart by the second bug fix.
 *
 * Nothing is asked that the model can work out for itself: no beat sheet, no
 * camera, no hotspot list. The brief step reads the author's line and enriches
 * it where it is thin.
 *
 * The ONE thing the author must supply for a motion is its LENGTH. The box
 * lives on a timeline next to everything else on the scene, so its length is a
 * layout decision, not a model's. The model only decides how much of that
 * length the content fills; when it finishes sooner, the finished picture
 * keeps moving in an ambient loop until the box ends.
 *
 * It follows the same contract with AddImageModal that AIImageGenerateTab
 * does: it registers its generate / reset / credit-estimate handlers upward
 * and reports its state, so the modal's footer drives all three tabs with one
 * button.
 */

const KINDS = {
  [SCENE_VISUAL_KINDS.THREE_D]: {
    icon: LuBox,
    titleKey: "SceneVisual.ThreeD.Title",
    titleFallback: "3B obje",
    placeholderKey: "SceneVisual.ThreeD.Placeholder",
    placeholderFallback: "Ne modellensin? Örn: dört odacıklı insan kalbi, kesiti açılabilsin",
    generatingKey: "SceneVisual.ThreeD.Generating",
    generatingFallback: "3B obje oluşturuluyor…",
    hintKey: "SceneVisual.ThreeD.Hint",
    hintFallback:
      "Kamera, etiketlenecek noktalar ve anlamlı etkileşimler yazdığın metinden çıkarılır.",
  },
  [SCENE_VISUAL_KINDS.MOTION]: {
    icon: LuWand,
    titleKey: "SceneVisual.Motion.Title",
    titleFallback: "Hareketli görsel",
    placeholderKey: "SceneVisual.Motion.Placeholder",
    placeholderFallback:
      "Ekranda ne hareket etsin? Örn: üç aşamalı süreç sırayla belirsin ve son aşama vurgulansın",
    generatingKey: "SceneVisual.Motion.Generating",
    generatingFallback: "Hareketli görsel oluşturuluyor…",
    hintKey: "SceneVisual.Motion.Hint",
    hintFallback: "Hareket sırasını metne bakarak model kurar; süreyi sen veriyorsun.",
  },
};

/**
 * Longest loop the "GIF" output renders. Mirrors SCENE_LOOP_MAX_SEC in the
 * backend (which enforces it before charging); kept here so the field can say
 * so before a request is ever made.
 */
const LOOP_MAX_SEC = 18;

/** Longest motion box the author can ask for (Standart output). */
const MOTION_MAX_SEC = 90;

const FIELD_LABEL = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: "#64748b",
  marginBottom: 8,
  textTransform: "uppercase",
};

const FIELD_INPUT = {
  width: "100%",
  height: 42,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #dce3f3",
  fontSize: 14,
  boxSizing: "border-box",
};

const SceneVisualGenerateTab = ({
  kind,
  narrationText,
  trainingId,
  navId,
  sceneIndex,
  sceneTitle,
  contentLanguage,
  onGenerateReady,
  onResetReady,
  onCreditCheckReady,
  onStateChange,
  onServerModal,
  onInsufficientBalance,
  onBoxGenerated,
  onImageGenerated,
  onMediaAdded,
}) => {
  const store = useStore();
  const dispatch = useDispatch();
  const copy = KINDS[kind];
  const isMotion = kind === SCENE_VISUAL_KINDS.MOTION;

  const [promptMode, setPromptMode] = useState("write");
  const [writePrompt, setWritePrompt] = useState("");
  const [narrationPrompt, setNarrationPrompt] = useState(narrationText || "");
  const [styleBrief, setStyleBrief] = useState("");
  const [model, setModel] = useState("");
  const [orientation, setOrientation] = useState("landscape");
  const [durationSec, setDurationSec] = useState("");
  // "motion" = playhead-driven MotionScene box; "gif" / "webp" = one seamless
  // cycle rendered to a looping animated image in that format and inserted as
  // an image. The two loop outputs share the design and differ only in file.
  // "mp4" = written like Standart, rendered once to an mp4 in the same job and
  // added as a video box — and, unlike Standart, it lands in the library.
  const [output, setOutput] = useState("motion");
  const isLoop = isMotion && (output === "gif" || output === "webp");
  const loopLabel = output === "webp" ? "WebP" : "GIF";
  const durationNumber = Number(durationSec);
  const durationValid =
    !!durationSec.trim() &&
    Number.isFinite(durationNumber) &&
    durationNumber >= 2 &&
    durationNumber <= (isLoop ? LOOP_MAX_SEC : MOTION_MAX_SEC);

  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // The narration arrives asynchronously (the modal fetches it when the scene
  // did not hand one over), so it is copied in whenever it changes and the
  // author has not typed over it.
  useEffect(() => {
    setNarrationPrompt((prev) => (prev ? prev : narrationText || ""));
  }, [narrationText]);

  useEffect(() => {
    onStateChange?.({ isGenerating, hasResult: !!result });
  }, [isGenerating, result, onStateChange]);

  const fields = useMemo(
    () => ({
      prompt: promptMode === "write" ? writePrompt.trim() : "",
      // Yalnızca kullanıcı "Anlatımdan" düğmesine bastıysa gider. Prompt
      // modunda sahnenin anlatımı arkadan EKLENMEZ: girdi kullanıcının
      // yazdığıdır, süreyi de kendisi veriyor.
      narrationText: promptMode === "narration" ? narrationPrompt.trim() : "",
      navId,
      sceneTitle,
      sceneIndex,
      contentLanguage,
      styleBrief: styleBrief.trim(),
      llmModel: model,
      trainingId,
      ...(isMotion
        ? {
            orientation,
            // Zorunlu: kutunun süresini kullanıcı belirler, model değil.
            durationSec: durationSec.trim() ? Number(durationSec) : undefined,
            output,
          }
        : {}),
    }),
    [
      output,
      promptMode,
      writePrompt,
      narrationPrompt,
      navId,
      sceneTitle,
      sceneIndex,
      contentLanguage,
      styleBrief,
      model,
      trainingId,
      isMotion,
      orientation,
      durationSec,
    ]
  );

  const generate = useCallback(async () => {
    const hasInput = promptMode === "write" ? !!writePrompt.trim() : !!narrationPrompt.trim();
    if (!hasInput) {
      onServerModal?.(
        i18n.t("SceneVisual.InputRequiredTitle") || "Açıklama gerekli",
        i18n.t("SceneVisual.InputRequired") ||
          "Ne oluşturmak istediğini yaz ya da sahnenin anlatımını kullan.",
        false
      );
      return;
    }

    // Süre motion'da ZORUNLU. Kutunun ne kadar yaşayacağını yazar belirler;
    // içerik daha kısa sürerse kalanı son karenin ortam döngüsü doldurur.
    // GIF'te ayrıca üst sınır var (backend de kredi düşmeden önce reddeder).
    if (isMotion && !durationValid) {
      onServerModal?.(
        i18n.t("SceneVisual.Motion.DurationRequiredTitle") || "Süre gerekli",
        isLoop
          ? i18n.t("SceneVisual.Motion.GifDurationRequired", {
              max: LOOP_MAX_SEC,
              format: loopLabel,
            }) || `${loopLabel} için 2 ile ${LOOP_MAX_SEC} saniye arasında bir süre yaz.`
          : i18n.t("SceneVisual.Motion.DurationRequired", { max: MOTION_MAX_SEC }) ||
              `Hareketli görselin kaç saniye süreceğini yaz (2 ile ${MOTION_MAX_SEC} sn arası).`,
        false
      );
      return;
    }

    setIsGenerating(true);
    setError(null);
    try {
      const generated = await generateSceneVisual(kind, fields);
      setResult(generated);
    } catch (err) {
      // An insufficient balance is not a failure to show in the tab: it has
      // its own modal, with the team-budget details the generic error box
      // cannot render.
      if (isCreditRefusal(err?.message, err?.data)) {
        onInsufficientBalance?.({
          ...(err.data || {}),
          message: err?.data?.message || err?.message,
        });
        return;
      }
      setError(err?.message || String(err));
    } finally {
      setIsGenerating(false);
    }
  }, [
    kind,
    fields,
    promptMode,
    writePrompt,
    narrationPrompt,
    isMotion,
    isLoop,
    loopLabel,
    durationValid,
    onServerModal,
    onInsufficientBalance,
  ]);

  const clearResults = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  const checkCredit = useCallback(async () => {
    const estimate = await estimateSceneVisual(kind, fields);
    return {
      requiredCredits: estimate.requiredCredits,
      availableCredits: estimate.availableCredits,
      hasSufficientBalance: estimate.hasSufficientBalance,
      providers: estimate.providers,
    };
  }, [kind, fields]);

  useEffect(() => {
    onGenerateReady?.(generate);
  }, [onGenerateReady, generate]);

  useEffect(() => {
    onResetReady?.(clearResults);
  }, [onResetReady, clearResults]);

  useEffect(() => {
    onCreditCheckReady?.(checkCredit);
  }, [onCreditCheckReady, checkCredit]);

  // GIF / WebP sonucu: kutu değil, görsel URL'i döner.
  const resultIsLoop = result?.kind === "gif" || result?.kind === "webp";
  const resultLoopLabel = result?.kind === "webp" ? "WebP" : "GIF";
  // MP4 sonucu: video URL'i döner, kütüphaneden mp4 eklenmiş gibi girer.
  const resultIsVideo = result?.kind === "video";

  const addToScene = useCallback(async () => {
    if (!result) return;
    if (resultIsVideo) {
      if (!result.videoUrl) return;
      const present = store.getState().undoGroup?.present || store.getState();
      const added = await addMediaLikeLibrary({
        store,
        dispatch,
        type: "video",
        url: result.videoUrl,
        navId: navId || present.navItemSelected,
        durationSec: result.durationSec,
      });
      if (!added) return;
      onMediaAdded?.();
    } else if (resultIsLoop) {
      // GIF / WebP çıktısı sıradan bir görsel: modalın görsel yolundan
      // (HotspotImages kutusu) eklenir, motion kutusu gibi playhead istemez.
      if (!result.imageUrl) return;
      // Kaynak notu: kutudaki "AI ile düzenle" bu dokümanı düzenleyip döngüyü
      // yeniden render eder. Görselin kendisi düzenlenemez — kaynağı bu.
      onImageGenerated?.(result.imageUrl, resultLoopLabel, {
        motionSource: result.htmlUrl
          ? {
              htmlUrl: result.htmlUrl,
              format: result.kind,
              orientation: result.orientation,
              model: result.model,
            }
          : undefined,
        // Timeline'da 0'dan bu süre kadar, motion kutusu gibi.
        durationSec: result.durationSec,
      });
    } else {
      if (!result.box || !result.toolbar) return;
      onBoxGenerated?.(result);
    }
    // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını kapat.
    approveJob(result.jobId);
    recordPopupResult({
      feature: kind === SCENE_VISUAL_KINDS.MOTION ? "motion" : "3d",
      summary: resultIsVideo
        ? "Added a motion graphic (MP4) to the scene"
        : resultIsLoop
          ? `Added a looping animation (${resultLoopLabel}) to the scene`
          : kind === SCENE_VISUAL_KINDS.MOTION
            ? "Added a motion graphic to the scene"
            : "Added a 3D object to the scene",
      url: result.videoUrl || result.imageUrl || result.htmlUrl,
    });
    setResult(null);
  }, [
    result,
    resultIsVideo,
    resultIsLoop,
    resultLoopLabel,
    kind,
    navId,
    store,
    dispatch,
    onBoxGenerated,
    onImageGenerated,
    onMediaAdded,
  ]);

  const Icon = copy.icon;

  if (isGenerating && !result) {
    return (
      <div className="add-image-modal__ai-tab">
        <div className="add-image-modal__ai-generating">
          <div className="add-image-modal__ai-generating-spinner" />
          <div className="add-image-modal__ai-generating-label">
            {i18n.t(copy.generatingKey) || copy.generatingFallback}
          </div>
          <div className="add-image-modal__ai-generating-meta">
            {i18n.t("SceneVisual.GeneratingMeta") ||
              "Model yazıyor — birkaç dakika sürebilir, modalı kapatabilirsin."}
          </div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="add-image-modal__ai-tab">
        <div className="add-image-modal__ai-result-card">
          <div className="add-image-modal__ai-result-card-header">
            <Icon /> {result.object || i18n.t(copy.titleKey) || copy.titleFallback}
            {isMotion && result.durationSec ? ` · ${result.durationSec.toFixed(1)}s` : ""}
            {resultIsLoop || resultIsVideo
              ? ` · ${resultIsVideo ? "MP4" : resultLoopLabel}` +
                (result.sizeBytes ? ` · ${(result.sizeBytes / 1024 / 1024).toFixed(1)} MB` : "")
              : ""}
          </div>

          {resultIsVideo ? (
            <video
              src={result.videoUrl}
              controls
              autoPlay
              muted
              playsInline
              style={{
                width: "100%",
                maxHeight: 360,
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                background: "#0b1020",
                display: "block",
              }}
            />
          ) : resultIsLoop ? (
            // Sonsuz dönen GIF / WebP: sahnede nasıl görünecekse burada da
            // öyle — düz bir <img>, playhead yok.
            <img
              src={result.imageUrl}
              alt={i18n.t(copy.titleKey) || copy.titleFallback}
              style={{
                width: "100%",
                maxHeight: 360,
                objectFit: "contain",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                background: "#0b1020",
                display: "block",
              }}
            />
          ) : (
            <SceneVisualPreview
              kind={kind}
              htmlUrl={result.htmlUrl}
              durationSec={result.durationSec}
            />
          )}

          {/* Why the model made the call it made. Short, and only when it
              actually said something — an empty rationale box is noise. */}
          {(result.brief?.lengthRationale || result.scope?.rationale) && (
            <div
              style={{
                marginTop: 10,
                fontSize: 13,
                lineHeight: 1.5,
                color: "#64748b",
              }}
            >
              {result.brief?.lengthRationale || result.scope?.rationale}
            </div>
          )}

          {!!result.warnings?.length && (
            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                lineHeight: 1.5,
                color: "#b45309",
                background: "rgba(245,158,11,0.08)",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              {result.warnings.slice(0, 3).join(" · ")}
            </div>
          )}

          <div className="add-image-modal__ai-result-card-actions">
            <button
              type="button"
              className="add-image-modal__ai-result-btn add-image-modal__ai-result-btn--ghost"
              onClick={clearResults}
            >
              {i18n.t("SceneVisual.Regenerate") || "Yeniden oluştur"}
            </button>
            <button
              type="button"
              className="add-image-modal__ai-result-btn add-image-modal__ai-result-btn--primary"
              onClick={addToScene}
            >
              {i18n.t("AddToScene") || "+ Sahneye Ekle"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-image-modal__ai-tab">
      <div className="add-image-modal__ai-section">
        <div className="add-image-modal__ai-prompt-toggle">
          <button
            type="button"
            className={promptMode === "write" ? "active" : ""}
            onClick={() => setPromptMode("write")}
          >
            <LuPenLine /> {i18n.t("WritePrompt") || "Prompt Yaz"}
          </button>
          {/* Yalnızca hareketli görselde: 3B objenin girdisi tek bir obje
              tarifi, bir sahnenin anlatımını ona çevirmenin anlamı yok. */}
          {isMotion && (
            <button
              type="button"
              className={promptMode === "narration" ? "active" : ""}
              onClick={() => setPromptMode("narration")}
              disabled={!narrationPrompt && !narrationText}
            >
              <LuFileText /> {i18n.t("FromNarration") || "Anlatımdan"}
            </button>
          )}
          <span className="add-image-modal__ai-prompt-badge">
            {i18n.t(copy.titleKey) || copy.titleFallback}
          </span>
        </div>

        {promptMode === "write" ? (
          <textarea
            className="add-image-modal__ai-prompt-input"
            value={writePrompt}
            onChange={(event) => setWritePrompt(event.target.value)}
            placeholder={i18n.t(copy.placeholderKey) || copy.placeholderFallback}
          />
        ) : (
          <div className="add-image-modal__ai-narration">
            <div className="add-image-modal__ai-narration-header">
              <span>
                <LuFileText /> {i18n.t("SceneNarration") || "Sahne Anlatımı"}
              </span>
            </div>
            <textarea
              className="add-image-modal__ai-prompt-input"
              value={narrationPrompt}
              onChange={(event) => setNarrationPrompt(event.target.value)}
              placeholder={i18n.t("SceneVisual.NarrationPlaceholder") || "Bu sahnenin anlatımı…"}
            />
          </div>
        )}

        <div className="add-image-modal__ai-prompt-footer">
          <span>{i18n.t(copy.hintKey) || copy.hintFallback}</span>
        </div>
      </div>

      <div className="add-image-modal__ai-section">
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "#64748b",
            marginBottom: 8,
            textTransform: "uppercase",
          }}
        >
          {i18n.t("SceneVisual.StyleBrief") || "Stil yönlendirmesi (opsiyonel)"}
        </label>
        <input
          type="text"
          value={styleBrief}
          onChange={(event) => setStyleBrief(event.target.value)}
          placeholder={
            i18n.t("SceneVisual.StyleBriefPlaceholder") ||
            "Palet, his, tipografi — örn: koyu zemin, turkuaz vurgu, sade"
          }
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #dce3f3",
            fontSize: 14,
            outline: "none",
          }}
        />
      </div>

      {/* Çıktı tipi — yalnızca hareketli görselde. Standart: sahnenin
          playhead'iyle oynayan motion kutusu. GIF / WebP: tek bir dikişsiz tur
          olarak tasarlanıp o formatta sonsuz dönen bir görsele çevrilir ve
          sahneye normal görsel olarak girer. İkisinin tasarımı aynı, yalnızca
          dosya formatı farklı. */}
      {isMotion && (
        <div className="add-image-modal__ai-section">
          <label style={FIELD_LABEL}>{i18n.t("SceneVisual.Output") || "Çıktı"}</label>
          <div className="add-image-modal__ai-prompt-toggle" style={{ marginBottom: 0 }}>
            <button
              type="button"
              className={output === "motion" ? "active" : ""}
              onClick={() => setOutput("motion")}
            >
              {i18n.t("SceneVisual.OutputMotion") || "Standart"}
            </button>
            <button
              type="button"
              className={output === "gif" ? "active" : ""}
              onClick={() => setOutput("gif")}
            >
              {i18n.t("SceneVisual.OutputGif") || "GIF"}
            </button>
            <button
              type="button"
              className={output === "webp" ? "active" : ""}
              onClick={() => setOutput("webp")}
            >
              {i18n.t("SceneVisual.OutputWebp") || "WebP"}
            </button>
            <button
              type="button"
              className={output === "mp4" ? "active" : ""}
              onClick={() => setOutput("mp4")}
            >
              {i18n.t("SceneVisual.OutputMp4") || "MP4"}
            </button>
          </div>
        </div>
      )}

      {/* Grid, flex değil: üç alan aynı hizada ve aynı aralıkla dursun. Süre
          açıklaması satırın ALTINDA tam genişlikte — sütunun içindeyken o
          sütunu uzatıyor, alanlar arasında düzensiz boşluk bırakıyordu. */}
      <div className="add-image-modal__ai-section">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMotion
              ? "minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)"
              : "1fr",
            gap: 12,
            alignItems: "end",
          }}
        >
          <div>
            <label style={FIELD_LABEL}>{i18n.t("SceneVisual.Model") || "Model"}</label>
            <select
              value={model}
              onChange={(event) => setModel(event.target.value)}
              style={FIELD_INPUT}
            >
              {SCENE_VISUAL_MODELS.map((option) => (
                <option key={option.value || "auto"} value={option.value}>
                  {i18n.t(option.labelKey) || option.fallback}
                </option>
              ))}
            </select>
          </div>

          {isMotion && (
            <div>
              <label style={FIELD_LABEL}>{i18n.t("SceneVisual.Orientation") || "Yön"}</label>
              <select
                value={orientation}
                onChange={(event) => setOrientation(event.target.value)}
                style={FIELD_INPUT}
              >
                <option value="landscape">16:9</option>
                <option value="portrait">9:16</option>
                <option value="square">1:1</option>
              </select>
            </div>
          )}

          {isMotion && (
            <div>
              <label style={FIELD_LABEL}>
                {i18n.t("SceneVisual.Duration") || "Süre (sn)"}{" "}
                <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="number"
                min="2"
                max={String(isLoop ? LOOP_MAX_SEC : MOTION_MAX_SEC)}
                required
                value={durationSec}
                onChange={(event) => setDurationSec(event.target.value)}
                placeholder={
                  isLoop
                    ? `max ${LOOP_MAX_SEC}`
                    : i18n.t("SceneVisual.DurationPlaceholder") || "örn. 15"
                }
                style={{
                  ...FIELD_INPUT,
                  border: `1px solid ${durationValid ? "#dce3f3" : "#fca5a5"}`,
                  outline: "none",
                }}
              />
            </div>
          )}
        </div>

        {isMotion && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>
            {isLoop
              ? i18n.t("SceneVisual.Motion.GifHint", { max: LOOP_MAX_SEC }) ||
                `Animasyon bu sürede tek bir tur atar ve sonsuz döngüye girer (en fazla ${LOOP_MAX_SEC} sn). Sahneye görsel olarak eklenir.`
              : i18n.t("SceneVisual.Motion.DurationHint") ||
                "Hareket daha kısa biterse kalan süre boyunca son hâli döngüyle oynamaya devam eder."}
          </div>
        )}
      </div>

      {error && (
        <div
          className="add-image-modal__ai-section"
          style={{ color: "#ef4444", fontSize: 13, lineHeight: 1.5 }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

SceneVisualGenerateTab.propTypes = {
  kind: PropTypes.oneOf(["3d", "motion"]).isRequired,
  narrationText: PropTypes.string,
  trainingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  navId: PropTypes.string,
  sceneIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sceneTitle: PropTypes.string,
  contentLanguage: PropTypes.string,
  onGenerateReady: PropTypes.func,
  onResetReady: PropTypes.func,
  onCreditCheckReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onServerModal: PropTypes.func,
  onInsufficientBalance: PropTypes.func,
  onBoxGenerated: PropTypes.func,
  /**
   * GIF / WebP output: (url, label) of the looping image, added through the
   * modal's image path.
   */
  onImageGenerated: PropTypes.func,
  /** MP4 output: the video box was added here; the modal should close. */
  onMediaAdded: PropTypes.func,
};

export default SceneVisualGenerateTab;
